import { announcementApi } from '@api/index';
import LoadingComponent from '@components/shared/loading/loadingComponent';
import BasicModal from '@/main/components/shared/modal/basicModal';
import { MarkAnnouncementReadRequest } from '@models/request/announcementRequest';
import { IAnnouncement } from '@models/response/announcementResponse';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import { useUserReducer } from '@store/user';
import { getTranslateByKey } from '@/main/services/utils';
import { SESSION_KEYNAME_ANNOUNCEMENT_CHECK } from '@/main/services/storage';
import { isNullUndefined } from '@/main/services/utils';
import { convertStringForBoolean } from '@/main/services/utilsString';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import AnnouncementPdfViewer from './announcementPdfViewer';
import * as S from './styles';

const ModalAnnouncements: React.FC = () => {
  const [queue, setQueue] = useState<IAnnouncement[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingAction, setProcessingAction] = useState(false);
  const [readComplete, setReadComplete] = useState(false);

  const { currentUser } = useUserReducer();

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const alreadyChecked = convertStringForBoolean(
      sessionStorage.getItem(SESSION_KEYNAME_ANNOUNCEMENT_CHECK)
    );

		if (!alreadyChecked && currentUser?.pendingAcceptanceTerms?.length === 0) {
			setLoading(true);
			loadAnnouncements();
		}
	}, [currentUser?.pendingAcceptanceTerms?.length]);

  const currentAnnouncement = queue[0];
  const isOpen = !!currentAnnouncement;

  useEffect(() => {
    setReadComplete(false);
  }, [currentAnnouncement?.guid]);

  const loadAnnouncements = async () => {
    if (isNullUndefined(currentUser)) return;
    try {
      const response = await announcementApi.getAnnouncementsByUser(
        currentUser.guidCompany
      );
      if (response.data.result && response.data.result.length > 0) {
        setQueue(response.data.result);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAction = useCallback(
    async (dontShowAgain: boolean, acceptMessageContent: boolean) => {
      if (!currentAnnouncement) return;

      setProcessingAction(true);
      try {
        const request: MarkAnnouncementReadRequest = {
          guid: currentAnnouncement.guid,
          dontShowMeAgain: dontShowAgain,
          acceptMessageContent: acceptMessageContent
        };
        await announcementApi.markAsRead(request);

        setQueue(prevQueue => prevQueue.slice(1));
        if (queue.at(-1)?.guid == currentAnnouncement.guid) {
          sessionStorage.setItem(SESSION_KEYNAME_ANNOUNCEMENT_CHECK, 'True');
        }
      } catch (error) {
        console.error(error);
      } finally {
        setProcessingAction(false);
      }
    },
    [currentAnnouncement]
  );

  if (!currentAnnouncement && !loading) return null;

  return (
    <BasicModal
      open={isOpen}
      disableOutsideClick={true}
      handleClose={() => handleAction(false, false)}
      boxStyle={{ width: '900px', maxWidth: '90vw' }}
      title={
        <div style={{ display: 'flex', justifyContent: 'left' }}>
          {getTranslateByKey('information.announcement')}
        </div>
      }>
      <S.ContainerModal>
        {loading || processingAction ? (
          <LoadingComponent />
        ) : (
          <>
            <S.AreaIconCloseModal>
              <IconButton
                aria-label="close"
                id="id-btn-close-announcement"
                onClick={() => handleAction(false, false)}>
                <CloseIcon />
              </IconButton>
            </S.AreaIconCloseModal>
            <AnnouncementPdfViewer
              key={currentAnnouncement.guid}
              pdfBase64={currentAnnouncement.base64}
              containerRef={containerRef}
              setReadComplete={setReadComplete}
            />

            <S.FooterContainer>
              {currentAnnouncement.displayDontShowMeAgain && (
                <>
                  <S.ButtonDontShowMeAgain
                    onClick={() => handleAction(true, false)}
                    id="id-btn-dont-show-me-again"
                    disabled={processingAction || !readComplete}>
                    {getTranslateByKey('information.dontShowMeAgain')}
                  </S.ButtonDontShowMeAgain>
                </>
              )}

              {currentAnnouncement.displayAcceptMessageContent && (
                <>
                  <S.ButtonDontShowMeAgain
                    onClick={() => handleAction(false, true)}
                    id="id-btn-accept-message-content"
                    disabled={processingAction || !readComplete}>
                    {getTranslateByKey('information.acceptMessageContent')}
                  </S.ButtonDontShowMeAgain>
                </>
              )}
            </S.FooterContainer>
          </>
        )}
      </S.ContainerModal>
    </BasicModal>
  );
};

export default ModalAnnouncements;
